import React, { useState, useEffect, useRef } from 'react';
import { fuzzySearch } from '../../utils/fuzzySearch';
import { FaBolt, FaStore, FaTruck, FaMoneyBillWave, FaCalendarAlt, FaClock, FaUser, FaMapMarkerAlt, FaEnvelope, FaPhone, FaPencilAlt, FaTrash, FaPrint, FaFileExcel, FaLock, FaPaperclip, FaFileInvoiceDollar, FaSpinner } from 'react-icons/fa';
import { generateOrderPDF } from '../../services/pdfService';
import { Order, OrderItem } from '../../types';
import styles from './styles/OrdersList.module.css';
import { useAuth } from '../../context/AuthContext';
import Pagination, { EMPTY_PAGINATION, PaginationMeta, readPaginated } from './Pagination';

// Parsea fechas YYYY-MM-DD como fecha LOCAL para evitar desfasaje UTC-3
const formatLocalDate = (dateStr: string): string => {
    if (!dateStr) return '-';
    // Si es formato YYYY-MM-DD (solo fecha, sin hora), parsear como local
    const isoDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
    if (isoDateOnly) {
        const [year, month, day] = dateStr.split('-');
        return `${day}/${month}/${year}`;
    }
    // Si tiene hora (ISO completo), usar toLocaleDateString normal
    return new Date(dateStr).toLocaleDateString('es-AR');
};

const formatLocalTime = (dateStr: string): string => {
    if (!dateStr) return '';
    const isoDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
    if (isoDateOnly) return '';
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
    } catch {
        return '';
    }
};

type TabType = 'Inmediato' | 'Retiro' | 'Envio' | 'Todas';

// Un envio a domicilio (incluye Venta Web) no se puede editar una vez creado:
// ya se imprimio y cualquier cambio descuadraria el comprobante despachado.
const TIPOS_ENVIO = ['Envio', 'Envío', 'Envio a domicilio', 'Envío a domicilio', 'Venta Web'];
const esEnvioDomicilio = (order: Order): boolean =>
    TIPOS_ENVIO.includes(order.TipoPedido) ||
    String(order.Envio || '').trim().toLowerCase() === 'si';

const OrdersList: React.FC = () => {
    const { user } = useAuth();
    const [orders, setOrders] = useState<Order[]>([]);
    // Paginacion: la tabla puede superar los 5000 pedidos.
    const [pagination, setPagination] = useState<PaginationMeta>(EMPTY_PAGINATION);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(50);
    const [loading, setLoading] = useState(true);
    const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<TabType>(user === 'Lu Garcia Vidal' ? 'Todas' : 'Inmediato');
    const [editingOrder, setEditingOrder] = useState<string | null>(null);
    const [editFormData, setEditFormData] = useState<Order | null>(null);
    const [editMixedPayments, setEditMixedPayments] = useState<{ metodo: string; monto: number }[]>([]);
    const [editIsMixed, setEditIsMixed] = useState(false);

    // Filter States
    const [filterDateStart, setFilterDateStart] = useState('');
    const [filterDateEnd, setFilterDateEnd] = useState('');
    const [filterClient, setFilterClient] = useState('');
    const [filterPayment, setFilterPayment] = useState('Todos');
    const [filterStatus, setFilterStatus] = useState('Todos');
    const [filterLocal, setFilterLocal] = useState('Todos');
    const [locales, setLocales] = useState<any[]>([]);
    const [products, setProducts] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);

    // Subida de comprobantes de pago
    const [uploadingComprobante, setUploadingComprobante] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const uploadTargetOrderId = useRef<string | null>(null);

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    useEffect(() => {
        fetchLocales();
        fetchProducts();
    }, []);

    useEffect(() => {
        fetchOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, page, perPage]);

    // Al cambiar de pestana o de tamano de pagina se vuelve al inicio: la
    // pagina actual podria no existir en el nuevo conjunto de resultados.
    useEffect(() => {
        setPage(1);
    }, [activeTab, perPage]);

    const fetchProducts = async () => {
        try {
            const res = await fetch(`${API_URL}/productos`);
            if (res.ok) {
                const data = await res.json();
                setProducts(data);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const fetchLocales = async () => {
        try {
            const res = await fetch(`${API_URL}/locales`);
            if (res.ok) {
                const data = await res.json();
                setLocales(data);
            }
        } catch (e) { console.error(e); }
    };

    const fetchOrders = () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (filterDateStart) params.append('fecha_inicio', filterDateStart);
        if (filterDateEnd) params.append('fecha_fin', filterDateEnd);
        if (filterClient) params.append('cliente', filterClient);
        if (filterPayment && filterPayment !== 'Todos') params.append('metodo_pago', filterPayment);
        if (filterStatus && filterStatus !== 'Todos') params.append('estado', filterStatus);
        if (filterLocal && filterLocal !== 'Todos') params.append('id_local', filterLocal);

        // Determinar tipo_tab para el backend
        let backendTab = activeTab;
        params.append('tipo_tab', backendTab);
        params.append('page', String(page));
        params.append('per_page', String(perPage));

        fetch(`${API_URL}/admin/orders?${params.toString()}`)
            .then(res => res.json())
            .then(data => {
                const { items, pagination: meta } = readPaginated<Order>(data);
                setOrders(items);
                setPagination(meta);
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setLoading(false);
            });
    };

    const togglePay = async (orderId: string, currentStatus: string) => {
        const newStatus = currentStatus === 'Si' ? 'No' : 'Si';
        try {
            const res = await fetch(`${API_URL}/admin/orders/${orderId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Pagado: newStatus })
            });
            if (res.ok) {
                // Actualizar localmente
                setOrders(orders.map(o => o.ID === orderId ? { ...o, Pagado: newStatus } : o));
            }
        } catch (error) {
            console.error(error);
            alert("Error al actualizar pago");
        }
    };

    // Alterna si un pedido reservado (Retiro) ya fue pasado a buscar por el cliente
    const toggleRetirado = async (orderId: string, currentStatus: number) => {
        const newStatus = currentStatus === 1 ? 0 : 1;
        try {
            const res = await fetch(`${API_URL}/admin/orders/${orderId}/retirado`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ retirado: newStatus })
            });
            if (res.ok) {
                setOrders(orders.map(o => o.ID === orderId ? { ...o, retirado: newStatus } : o));
            } else {
                alert("Error al actualizar el estado de retiro");
            }
        } catch (error) {
            console.error(error);
            alert("Error de conexión");
        }
    };

    // Abre el selector de archivo para subir el comprobante de un pedido
    const handleComprobanteClick = (orderId: string) => {
        uploadTargetOrderId.current = orderId;
        if (fileInputRef.current) {
            fileInputRef.current.value = ''; // permitir re-subir el mismo archivo
            fileInputRef.current.click();
        }
    };

    const handleComprobanteSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        const orderId = uploadTargetOrderId.current;
        if (!file || !orderId) return;

        const MAX_BYTES = 6 * 1024 * 1024;
        if (file.size > MAX_BYTES) {
            alert('El archivo supera el máximo de 6 MB.');
            return;
        }

        setUploadingComprobante(orderId);
        try {
            const formData = new FormData();
            formData.append('comprobante', file);
            const res = await fetch(`${API_URL}/admin/orders/${orderId}/comprobante`, {
                method: 'POST',
                body: formData,
            });
            if (res.ok) {
                const data = await res.json();
                setOrders(orders.map(o => o.ID === orderId ? { ...o, ComprobanteUrl: data.ComprobanteUrl, Pagado: data.Pagado || 'Si' } : o));
            } else {
                const err = await res.json().catch(() => ({}));
                alert(err.error || 'Error al subir el comprobante');
            }
        } catch (error) {
            console.error(error);
            alert('Error de conexión al subir el comprobante');
        } finally {
            setUploadingComprobante(null);
            uploadTargetOrderId.current = null;
        }
    };

    const handleDeleteOrder = async (orderId: string) => {
        if (!window.confirm("¿Está seguro de que desea eliminar este pedido? Esta acción no se puede deshacer.")) {
            return;
        }

        try {
            const res = await fetch(`${API_URL}/admin/orders/${orderId}`, {
                method: 'DELETE'
            });
            if (res.ok) {
                setOrders(orders.filter(o => o.ID !== orderId));
                alert("Pedido eliminado");
            } else {
                alert("Error al eliminar el pedido");
            }
        } catch (error) {
            console.error(error);
            alert("Error de conexión");
        }
    };

    const handleStartEdit = (order: Order) => {
        if (esEnvioDomicilio(order)) {
            alert("Este pedido es un envío a domicilio y no puede editarse una vez creado, porque ya fue impreso. Si hay un error, eliminá el pedido y creá uno nuevo.");
            return;
        }
        setEditingOrder(order.ID);
        setEditFormData({ ...order });
        setExpandedOrder(order.ID);
        const isMixed = order.MetodoPago === 'Mixto';
        setEditIsMixed(isMixed);
        setEditMixedPayments(isMixed && order.pagos_mixtos?.length ? order.pagos_mixtos : [{ metodo: '', monto: 0 }, { metodo: '', monto: 0 }]);
    };

    const handleCancelEdit = () => {
        setEditingOrder(null);
        setEditFormData(null);
        setEditIsMixed(false);
        setEditMixedPayments([]);
    };

    const handleSaveEdit = async () => {
        if (!editFormData) return;

        try {
            const res = await fetch(`${API_URL}/admin/orders/${editFormData.ID}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    Cliente: editFormData.Cliente,
                    DNI: editFormData.DNI,
                    Direccion: editFormData.Direccion,
                    Telefono: editFormData.Telefono,
                    Email: editFormData.Email,
                    MetodoPago: editIsMixed ? 'Mixto' : editFormData.MetodoPago,
                    pagos_mixtos: editIsMixed ? editMixedPayments.filter(p => p.metodo && p.monto > 0) : null,
                    Pagado: editFormData.Pagado,
                    TipoPedido: editFormData.TipoPedido,
                    FechaEntrega: editFormData.FechaEntrega,
                    HorarioEntrega: editFormData.HorarioEntrega,
                    RestriccionHorario: editFormData.RestriccionHorario || '',
                    Monto: editFormData.Total,
                    id_local: editFormData.id_local ? Number(editFormData.id_local) : null,
                    Items: editFormData.Items
                })
            });

            if (res.ok) {
                setOrders(orders.map(o => o.ID === editFormData.ID ? { ...editFormData } : o));
                setEditingOrder(null);
                setEditFormData(null);
                alert("Pedido actualizado correctamente");
            } else {
                alert("Error al actualizar el pedido");
            }
        } catch (error) {
            console.error(error);
            alert("Error de conexión");
        }
    };

    const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        if (!editFormData) return;
        const { name, value } = e.target;
        setEditFormData({
            ...editFormData,
            [name]: value
        });
    };

    const toggleExpand = (orderId: string) => {
        setExpandedOrder(expandedOrder === orderId ? null : orderId);
    };

    const EDIT_DISCOUNT_HIGH = 1000;
    const EDIT_DISCOUNT_LOW = 500;
    const EDIT_PRICE_THRESHOLD = 5000;

    const calcItemDiscount = (precio: number, cantidad: number) =>
        Math.floor(cantidad / 2) * (precio >= EDIT_PRICE_THRESHOLD ? EDIT_DISCOUNT_HIGH : EDIT_DISCOUNT_LOW);

    const calcTotal = (items: Order['Items']) =>
        (items || []).reduce((acc, item) => acc + (item.Cantidad * (item.Precio || 0)) - (item.Descuento || 0), 0);

    const handleItemQuantityChange = (idx: number, newQty: number) => {
        if (!editFormData) return;
        const newItems = [...editFormData.Items];
        if (newQty < 1) {
            newItems.splice(idx, 1);
        } else {
            const item = newItems[idx];
            // Si tenía descuento aplicado, recalcularlo con la nueva cantidad
            const descuento = (item.Descuento || 0) > 0 && newQty >= 2
                ? calcItemDiscount(item.Precio || 0, newQty)
                : 0;
            newItems[idx] = { ...item, Cantidad: newQty, Descuento: descuento };
        }
        setEditFormData({ ...editFormData, Items: newItems, Total: calcTotal(newItems) });
    };

    const toggleEditItemDiscount = (idx: number) => {
        if (!editFormData) return;
        const newItems = [...editFormData.Items];
        const item = newItems[idx];
        const tieneDescuento = (item.Descuento || 0) > 0;
        newItems[idx] = {
            ...item,
            Descuento: tieneDescuento ? 0 : calcItemDiscount(item.Precio || 0, item.Cantidad)
        };
        setEditFormData({ ...editFormData, Items: newItems, Total: calcTotal(newItems) });
    };

    const handleRemoveItem = (idx: number) => {
        if (!editFormData) return;
        const newItems = [...editFormData.Items];
        newItems.splice(idx, 1);
        setEditFormData({ ...editFormData, Items: newItems, Total: calcTotal(newItems) });
    };

    const handleAddProductToOrder = (product: any) => {
        if (!editFormData) return;
        const newItems = [...editFormData.Items];
        const existingIdx = newItems.findIndex(i => i.idProductos === product.idProductos);

        if (existingIdx !== -1) {
            newItems[existingIdx] = { ...newItems[existingIdx], Cantidad: newItems[existingIdx].Cantidad + 1 };
        } else {
            newItems.push({
                Producto: product.NombresProductos,
                Cantidad: 1,
                Precio: product.Precio,
                idProductos: product.idProductos
            });
        }

        setEditFormData({ ...editFormData, Items: newItems, Total: calcTotal(newItems) });
        setSearchTerm('');
        setSearchResults([]);
    };

    useEffect(() => {
        if (!searchTerm || searchTerm.length < 2) {
            setSearchResults([]);
            return;
        }
        const results = fuzzySearch(products, searchTerm, {
            keys: ['NombresProductos', 'idProductos'],
            threshold: 0.35
        }).slice(0, 10);
        setSearchResults(results);
    }, [searchTerm, products]);

    const horarioStart = (h?: string): string => {
        if (!h) return '99:99';
        const m = h.match(/(\d{1,2}):(\d{2})/);
        if (!m) return '99:99';
        return `${m[1].padStart(2, '0')}:${m[2]}`;
    };

    const dateFilterActive = !!(filterDateStart || filterDateEnd);

    const baseFiltered = orders.filter(order => {
        if (activeTab === 'Todas') return true;
        if (activeTab === 'Inmediato') return order.TipoPedido === 'Inmediato';
        if (activeTab === 'Retiro') return order.TipoPedido === 'Retiro';
        if (activeTab === 'Envio') return order.TipoPedido === 'Envio' || order.TipoPedido === 'Venta Web';
        return true;
    });

    const filteredOrders = dateFilterActive
        ? [...baseFiltered].sort((a, b) => {
            const fechaA = a.FechaEntrega || a.Fecha || '';
            const fechaB = b.FechaEntrega || b.Fecha || '';
            if (fechaA !== fechaB) return fechaA.localeCompare(fechaB);
            const horaA = horarioStart(a.HorarioEntrega);
            const horaB = horarioStart(b.HorarioEntrega);
            if (horaA !== horaB) return horaA.localeCompare(horaB);
            return Number(a.ID) - Number(b.ID);
        })
        : baseFiltered;

    const exportToCSV = () => {
        if (filteredOrders.length === 0) return;
        const BOM = '\uFEFF';
        const headers = ['N° Pedido', 'Fecha Entrega', 'Horario', 'Restricción Horario', 'Local / Dirección', 'Cliente', 'DNI', 'Telefono', 'Tipo', 'Metodo Pago', 'Estado Pago', 'Total', 'Productos', 'Observaciones'];
        const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const rows = filteredOrders.map(o => [
            o.ID,
            o.FechaEntrega ? formatLocalDate(o.FechaEntrega) : formatLocalDate(o.Fecha),
            esc(o.HorarioEntrega || formatLocalTime(o.Fecha)),
            esc(o.RestriccionHorario || ''),
            esc((o.TipoPedido === 'Envio' || o.TipoPedido === 'Venta Web') ? o.Direccion : (locales.find(l => l.locales_id == o.id_local)?.nombre_local ?? 'Global')),
            esc(o.Cliente),
            esc(o.DNI),
            esc(o.Telefono),
            esc(o.TipoPedido),
            esc(o.MetodoPago),
            o.Pagado === 'Si' ? 'Pagado' : 'Pendiente',
            Number(o.Total),
            esc(o.Items.map(i => `${i.Cantidad}x ${i.Producto}`).join(' | ')),
            esc(o.Observaciones),
        ]);
        const csv = BOM + [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const tabLabel = activeTab.toLowerCase();
        const desde = filterDateStart || 'inicio';
        const hasta = filterDateEnd || 'hoy';
        a.download = `pedidos_${tabLabel}_${desde}_${hasta}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (loading) return <div>Cargando pedidos...</div>;

    return (
        <div className={styles['orders-container']}>
            {/* Input oculto compartido para subir comprobantes de pago */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,application/pdf"
                style={{ display: 'none' }}
                onChange={handleComprobanteSelected}
            />
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            <div className={styles['orders-tabs']}>
                {/* Local Selector Logic integrated into filters, but user asked for "views per local" */}
                {/*Tabs for Order Type*/}

                <button
                    className={`${styles['tab-btn']} ${activeTab === 'Inmediato' ? styles.active : ''}`}
                    onClick={() => setActiveTab('Inmediato')}
                >
                    <FaBolt /> Inmediatos
                </button>
                <button
                    className={`${styles['tab-btn']} ${activeTab === 'Retiro' ? styles.active : ''}`}
                    onClick={() => setActiveTab('Retiro')}
                >
                    <FaStore /> Retiro
                </button>
                <button
                    className={`${styles['tab-btn']} ${activeTab === 'Envio' ? styles.active : ''}`}
                    onClick={() => setActiveTab('Envio')}
                >
                    <FaTruck /> Envíos
                </button>
                <button
                    className={`${styles['tab-btn']} ${activeTab === 'Todas' ? styles.active : ''}`}
                    onClick={() => setActiveTab('Todas')}
                >
                    Todas
                </button>
            </div>

            {/* Filter Bar */}
            <div className={styles['filters-bar']}>
                <div className={styles['filter-group']}>
                    <label>Local</label>
                    <select
                        className={styles['filter-input']}
                        value={filterLocal}
                        onChange={(e) => setFilterLocal(e.target.value)}
                    >
                        <option value="Todos">Todos los Locales</option>
                        {locales.map(l => (
                            <option key={l.locales_id} value={l.locales_id}>{l.nombre_local}</option>
                        ))}
                    </select>
                </div>
                <div className={styles['filter-group']}>
                    <label>{(activeTab === 'Envio' || activeTab === 'Retiro' || activeTab === 'Todas') ? 'Entrega Desde' : 'Desde'}</label>
                    <input
                        type="date"
                        className={styles['filter-input']}
                        value={filterDateStart}
                        onChange={(e) => setFilterDateStart(e.target.value)}
                    />
                </div>
                <div className={styles['filter-group']}>
                    <label>{(activeTab === 'Envio' || activeTab === 'Retiro' || activeTab === 'Todas') ? 'Entrega Hasta' : 'Hasta'}</label>
                    <input
                        type="date"
                        className={styles['filter-input']}
                        value={filterDateEnd}
                        onChange={(e) => setFilterDateEnd(e.target.value)}
                    />
                </div>
                <div className={styles['filter-group']}>
                    <label>Cliente / ID</label>
                    <input
                        type="text"
                        placeholder="Nombre, DNI o N° pedido"
                        className={styles['filter-input']}
                        value={filterClient}
                        onChange={(e) => setFilterClient(e.target.value)}
                    />
                </div>
                <div className={styles['filter-group']}>
                    <label>Tipo de Pago</label>
                    <select
                        className={styles['filter-input']}
                        value={filterPayment}
                        onChange={(e) => setFilterPayment(e.target.value)}
                    >
                        <option value="Todos">Todos</option>
                        <option value="Efectivo">Efectivo</option>
                        <option value="MercadoPago">MercadoPago</option>
                        <option value="Transferencia">Transferencia</option>
                        <option value="QR">QR</option>
                        <option value="Debito">Débito</option>
                        <option value="Credito">Crédito</option>
                        <option value="Mixto">Mixto</option>
                    </select>
                </div>
                <div className={styles['filter-group']}>
                    <label>Estado Pago</label>
                    <select
                        className={styles['filter-input']}
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        <option value="Todos">Todos</option>
                        <option value="Si">Pagado</option>
                        <option value="No">Pendiente</option>
                    </select>
                </div>
                <button className={styles['filter-btn']} onClick={fetchOrders}>
                    Filtrar
                </button>
                <button
                    className={`${styles['filter-btn']} ${styles['btn-clear']}`}
                    onClick={() => {
                        setFilterDateStart('');
                        setFilterDateEnd('');
                        setFilterClient('');
                        setFilterPayment('Todos');
                        setFilterStatus('Todos');
                        setFilterLocal('Todos');
                        setTimeout(fetchOrders, 0);
                    }}
                >
                    Limpiar
                </button>
                <button
                    onClick={exportToCSV}
                    disabled={filteredOrders.length === 0}
                    title={`Exportar ${filteredOrders.length} pedidos a Excel`}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        background: '#1d6f42', color: '#fff', border: 'none',
                        borderRadius: '6px', padding: '6px 14px', cursor: 'pointer',
                        fontSize: '0.85rem', fontWeight: 600,
                        opacity: filteredOrders.length === 0 ? 0.4 : 1,
                    }}
                >
                    <FaFileExcel /> Exportar ({filteredOrders.length})
                </button>
            </div>

            <div className={styles['orders-table-container']}>
                <table className={styles['orders-table']}>
                    <thead>
                        <tr>
                            <th>N° Pedido</th>
                            <th>
                                {activeTab === 'Envio' ? 'Fecha de Envío'
                                    : activeTab === 'Retiro' ? 'Fecha de Retiro'
                                        : 'Fecha Entrega'}
                            </th>
                            <th>Local / Dirección</th>
                            <th>Cliente</th>
                            <th>Total</th>
                            <th>Estado Pago</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredOrders.length === 0 ? (
                            <tr>
                                <td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>
                                    No hay pedidos que coincidan con los filtros.
                                </td>
                            </tr>
                        ) : (
                            filteredOrders.map((order, idx) => {
                                const prevOrder = idx > 0 ? filteredOrders[idx - 1] : null;
                                const currHorario = order.HorarioEntrega || '';
                                const prevHorario = prevOrder?.HorarioEntrega || '';
                                const currFecha = order.FechaEntrega || '';
                                const prevFecha = prevOrder?.FechaEntrega || '';
                                const showSeparator = dateFilterActive && (prevOrder === null || currHorario !== prevHorario || currFecha !== prevFecha);
                                return (
                                <React.Fragment key={order.ID}>
                                    {showSeparator && (
                                        <tr className={styles['horario-separator']}>
                                            <td colSpan={7}>
                                                <FaClock style={{ marginRight: '6px' }} />
                                                {currFecha ? formatLocalDate(currFecha) : 'Sin fecha'}
                                                {currHorario && <> · <strong>{currHorario}</strong></>}
                                            </td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td><strong>#{order.ID}</strong></td>
                                        <td>
                                            {order.FechaEntrega ? (
                                                <span>
                                                    {formatLocalDate(order.FechaEntrega)}
                                                    {order.HorarioEntrega && (
                                                        <> · <strong>{order.HorarioEntrega}</strong></>
                                                    )}
                                                    {order.RestriccionHorario && (
                                                        <>
                                                            <br />
                                                            <span className={styles['restriccion-horario-badge']}>
                                                                ⏰ {order.RestriccionHorario}
                                                            </span>
                                                        </>
                                                    )}
                                                    <br />
                                                    <small style={{ color: '#888', fontSize: '0.75em' }}>
                                                        {
                                                            order.TipoPedido === 'Envio' || order.TipoPedido === 'Venta Web'
                                                                ? '🚚 Envío'
                                                                : order.TipoPedido === 'Retiro'
                                                                    ? '🏪 Retiro'
                                                                    : '⚡ Inmediato'
                                                        }
                                                    </small>
                                                </span>
                                            ) : (
                                                <span>
                                                    {formatLocalDate(order.Fecha)}
                                                    <br />
                                                    <small style={{ color: '#888', fontSize: '0.75em' }}>⚡ Inmediato</small>
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <span style={{ fontSize: '0.85em', color: '#555' }}>
                                                {(order.TipoPedido === 'Envio' || order.TipoPedido === 'Venta Web')
                                                    ? order.Direccion
                                                    : (locales.find(l => l.locales_id == order.id_local)?.nombre_local || 'Global')}
                                            </span>
                                        </td>
                                        <td>
                                            <strong>{order.Cliente}</strong><br />
                                            <small>{order.Contacto}</small>
                                        </td>
                                        <td>${Number(order.Total).toLocaleString()}</td>
                                        <td>
                                            <span className={`${styles['status-badge']} ${order.Pagado === 'Si' ? styles['status-pagado'] : styles['status-pendiente']}`}>
                                                {order.Pagado === 'Si' ? 'PAGADO' : 'PENDIENTE'}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <button
                                                    className={`${styles['btn-action']} ${styles['btn-details']}`}
                                                    onClick={() => toggleExpand(order.ID)}
                                                    title="Ver Detalle"
                                                >
                                                    {expandedOrder === order.ID ? 'Ocultar' : 'Detalle'}
                                                </button>
                                                <button
                                                    className={`${styles['btn-action']} ${styles['btn-pay']}`}
                                                    onClick={() => togglePay(order.ID, order.Pagado)}
                                                    title={order.Pagado === 'Si' ? 'Marcar como Pendiente' : 'Marcar como Pagado'}
                                                >
                                                    {order.Pagado === 'Si' ? 'Cobrado' : 'Pagado'}
                                                </button>
                                                {order.TipoPedido === 'Retiro' && (
                                                    <button
                                                        className={styles['btn-action']}
                                                        onClick={() => toggleRetirado(order.ID, order.retirado || 0)}
                                                        title={order.retirado === 1 ? 'Marcar como NO retirado (pendiente de pasar a buscar)' : 'Marcar como RETIRADO (el cliente ya pasó a buscarlo)'}
                                                        style={{
                                                            backgroundColor: order.retirado === 1 ? '#27ae60' : '#e74c3c',
                                                            color: 'white',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {order.retirado === 1 ? '✓ Retirado' : 'Sin retirar'}
                                                    </button>
                                                )}
                                                <button
                                                    className={`${styles['btn-action']} ${styles['btn-print']}`}
                                                    onClick={() => generateOrderPDF(order)}
                                                    title="Imprimir Ticket/Recibo"
                                                    style={{ backgroundColor: '#9c27b0', color: 'white' }}
                                                >
                                                    <FaPrint />
                                                </button>
                                                {order.ComprobanteUrl ? (
                                                    <button
                                                        className={styles['btn-action']}
                                                        onClick={() => window.open(`${API_URL}${order.ComprobanteUrl}`, '_blank')}
                                                        title="Ver comprobante de pago"
                                                        style={{ backgroundColor: '#27ae60', color: 'white' }}
                                                    >
                                                        <FaFileInvoiceDollar />
                                                    </button>
                                                ) : (
                                                    <button
                                                        className={styles['btn-action']}
                                                        onClick={() => handleComprobanteClick(order.ID)}
                                                        disabled={uploadingComprobante === order.ID}
                                                        title="Subir comprobante de pago (imagen o PDF)"
                                                        style={{ backgroundColor: '#607d8b', color: 'white' }}
                                                    >
                                                        {uploadingComprobante === order.ID
                                                            ? <FaSpinner className={styles['spin'] || ''} style={{ animation: 'spin 1s linear infinite' }} />
                                                            : <FaPaperclip />}
                                                    </button>
                                                )}
                                                {esEnvioDomicilio(order) ? (
                                                    <button
                                                        className={`${styles['btn-action']} ${styles['btn-edit']}`}
                                                        disabled
                                                        title="Los envíos a domicilio no se pueden editar una vez creados (ya fueron impresos)"
                                                        style={{ opacity: 0.4, cursor: 'not-allowed' }}
                                                    >
                                                        <FaLock />
                                                    </button>
                                                ) : (
                                                    <button
                                                        className={`${styles['btn-action']} ${styles['btn-edit']}`}
                                                        onClick={() => handleStartEdit(order)}
                                                        title="Editar Pedido"
                                                    >
                                                        <FaPencilAlt />
                                                    </button>
                                                )}
                                                <button
                                                    className={`${styles['btn-action']} ${styles['btn-delete']}`}
                                                    onClick={() => handleDeleteOrder(order.ID)}
                                                    title="Eliminar Pedido"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    {expandedOrder === order.ID && (
                                        <tr className={styles['order-details-row']}>
                                            <td colSpan={7}>
                                                {editingOrder === order.ID ? (
                                                    <div className={styles['edit-form-container']}>
                                                        <h3 style={{ marginBottom: '1.5rem', color: 'var(--secondary-color)' }}>Editar Pedido #{order.ID}</h3>
                                                        <div className={styles['edit-grid']}>
                                                            <div>
                                                                <div className={styles['edit-field']}>
                                                                    <label>Cliente:</label>
                                                                    <input type="text" name="Cliente" value={editFormData?.Cliente || ''} onChange={handleEditChange} />
                                                                </div>
                                                                <div className={styles['edit-field']}>
                                                                    <label>DNI:</label>
                                                                    <input type="text" name="DNI" value={editFormData?.DNI || ''} onChange={handleEditChange} />
                                                                </div>
                                                                <div className={styles['edit-field']}>
                                                                    <label>Teléfono:</label>
                                                                    <input type="text" name="Telefono" value={editFormData?.Telefono || ''} onChange={handleEditChange} />
                                                                </div>
                                                                <div className={styles['edit-field']}>
                                                                    <label>Email:</label>
                                                                    <input type="email" name="Email" value={editFormData?.Email || ''} onChange={handleEditChange} />
                                                                </div>
                                                                <div className={styles['edit-field']}>
                                                                    <label>Monto Total:</label>
                                                                    <input type="number" name="Total" value={editFormData?.Total || 0} onChange={(e) => setEditFormData(prev => prev ? { ...prev, Total: Number(e.target.value) } : null)} />
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <div className={styles['edit-field']}>
                                                                    <label>Dirección:</label>
                                                                    <input type="text" name="Direccion" value={editFormData?.Direccion || ''} onChange={handleEditChange} />
                                                                </div>
                                                                <div className={styles['edit-field']}>
                                                                    <label>Fecha Entrega:</label>
                                                                    <input type="date" name="FechaEntrega" value={editFormData?.FechaEntrega || ''} onChange={handleEditChange} />
                                                                </div>
                                                                <div className={styles['edit-field']}>
                                                                    <label>Horario:</label>
                                                                    <select name="HorarioEntrega" value={editFormData?.HorarioEntrega || ''} onChange={handleEditChange}>
                                                                        <option value="09:00 a 12:00">09:00 a 12:00</option>
                                                                        <option value="12:00 a 15:00">12:00 a 15:00</option>
                                                                        {editFormData?.TipoPedido === 'Retiro' && (
                                                                            <option value="15:00 a 22:00">15:00 a 22:00</option>
                                                                        )}
                                                                        {editFormData?.HorarioEntrega && !['09:00 a 12:00', '12:00 a 15:00', '15:00 a 22:00'].includes(editFormData.HorarioEntrega) && (
                                                                            <option value={editFormData.HorarioEntrega}>{editFormData.HorarioEntrega}</option>
                                                                        )}
                                                                    </select>
                                                                </div>
                                                                <div className={styles['edit-field']}>
                                                                    <label>Restricción Horario:</label>
                                                                    <input
                                                                        type="text"
                                                                        name="RestriccionHorario"
                                                                        value={editFormData?.RestriccionHorario || ''}
                                                                        onChange={handleEditChange}
                                                                        placeholder="Ej: ANTES DE LAS 10 (opcional)"
                                                                    />
                                                                </div>
                                                                <div className={styles['edit-field']}>
                                                                    <label>Método Pago:</label>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                                        {!editIsMixed && (
                                                                            <select name="MetodoPago" value={editFormData?.MetodoPago === 'Mixto' ? '' : (editFormData?.MetodoPago || '')} onChange={handleEditChange}>
                                                                                <option value="Efectivo">Efectivo</option>
                                                                                <option value="MercadoPago">MercadoPago</option>
                                                                                <option value="Transferencia">Transferencia</option>
                                                                                <option value="QR">QR</option>
                                                                                <option value="Debito">Débito</option>
                                                                                <option value="Credito">Crédito</option>
                                                                            </select>
                                                                        )}
                                                                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85em', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                                            <input type="checkbox" checked={editIsMixed} onChange={e => setEditIsMixed(e.target.checked)} />
                                                                            Pago mixto
                                                                        </label>
                                                                    </div>
                                                                    {editIsMixed && (
                                                                        <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                            {editMixedPayments.map((p, i) => (
                                                                                <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                                                    <select
                                                                                        value={p.metodo}
                                                                                        onChange={e => setEditMixedPayments(prev => prev.map((x, idx) => idx === i ? { ...x, metodo: e.target.value } : x))}
                                                                                        style={{ padding: '4px', borderRadius: '4px', border: '1px solid #ccc' }}
                                                                                    >
                                                                                        <option value="">Método...</option>
                                                                                        <option value="Efectivo">Efectivo</option>
                                                                                        <option value="MercadoPago">MercadoPago</option>
                                                                                        <option value="Transferencia">Transferencia</option>
                                                                                        <option value="QR">QR</option>
                                                                                        <option value="Debito">Débito</option>
                                                                                        <option value="Credito">Crédito</option>
                                                                                    </select>
                                                                                    <input
                                                                                        type="number"
                                                                                        value={p.monto || ''}
                                                                                        onChange={e => setEditMixedPayments(prev => prev.map((x, idx) => idx === i ? { ...x, monto: Number(e.target.value) } : x))}
                                                                                        placeholder="$"
                                                                                        style={{ width: '90px', padding: '4px', borderRadius: '4px', border: '1px solid #ccc' }}
                                                                                    />
                                                                                    {editMixedPayments.length > 2 && (
                                                                                        <button onClick={() => setEditMixedPayments(prev => prev.filter((_, idx) => idx !== i))} style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '1em' }}>×</button>
                                                                                    )}
                                                                                </div>
                                                                            ))}
                                                                            <button onClick={() => setEditMixedPayments(prev => [...prev, { metodo: '', monto: 0 }])} style={{ alignSelf: 'flex-start', padding: '3px 10px', fontSize: '0.8em', borderRadius: '4px', border: '1px solid #3498db', background: 'transparent', color: '#3498db', cursor: 'pointer' }}>
                                                                                + Agregar método
                                                                            </button>
                                                                            <span style={{ fontSize: '0.8em', color: editMixedPayments.reduce((a, x) => a + x.monto, 0) === Number(editFormData?.Total) ? '#27ae60' : '#e74c3c', fontWeight: 'bold' }}>
                                                                                Suma: ${editMixedPayments.reduce((a, x) => a + x.monto, 0).toLocaleString()} / Total: ${Number(editFormData?.Total).toLocaleString()}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className={styles['edit-field']}>
                                                                    <label>Local de Gestión:</label>
                                                                    <select name="id_local" value={editFormData?.id_local || ''} onChange={handleEditChange}>
                                                                        <option value="">Sin Local (Global)</option>
                                                                        {locales.map(l => (
                                                                            <option key={l.locales_id} value={l.locales_id}>{l.nombre_local}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                                <div className={styles['edit-field']}>
                                                                    <label>Observaciones:</label>
                                                                    <textarea name="Observaciones" value={editFormData?.Observaciones || ''} onChange={handleEditChange} rows={3} />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Items Editor */}
                                                        <div className={styles['edit-items-section']}>
                                                            <h4>Modificar Productos</h4>
                                                            <div className={styles['product-search-wrapper']}>
                                                                <input
                                                                    type="text"
                                                                    placeholder="Agregar producto (nombre o ID)..."
                                                                    value={searchTerm}
                                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                                    className={styles['edit-search-input']}
                                                                />
                                                                {searchResults.length > 0 && (
                                                                    <div className={styles['edit-search-results']}>
                                                                        {searchResults.map(p => (
                                                                            <div key={p.idProductos} className={styles['edit-search-item']} onClick={() => handleAddProductToOrder(p)}>
                                                                                <span>{p.NombresProductos}</span>
                                                                                <strong>${p.Precio}</strong>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <table className={styles['edit-items-table']}>
                                                                <thead>
                                                                    <tr>
                                                                        <th>Producto</th>
                                                                        <th>Precio</th>
                                                                        <th>Cantidad</th>
                                                                        <th>Desc.</th>
                                                                        <th>Subtotal</th>
                                                                        <th></th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {editFormData?.Items.map((item, idx) => {
                                                                        const subtotal = (item.Cantidad * (item.Precio || 0)) - (item.Descuento || 0);
                                                                        const puedeDescontar = item.Cantidad >= 2;
                                                                        return (
                                                                            <tr key={idx}>
                                                                                <td>
                                                                                    {item.Producto}
                                                                                    {((item.DetallePromocion || item.PromoSelecciones || []).length > 0) && (
                                                                                        <ul className={styles['promo-selections']}>
                                                                                            {(item.DetallePromocion || item.PromoSelecciones || []).map((selection, selectionIdx) => (
                                                                                                <li key={`${selection.id_producto}-${selectionIdx}`}>
                                                                                                    {selection.grupo ? `${selection.grupo}: ` : ''}{selection.cantidad}x {selection.nombre}
                                                                                                </li>
                                                                                            ))}
                                                                                        </ul>
                                                                                    )}
                                                                                </td>
                                                                                <td>${(item.Precio || 0).toLocaleString()}</td>
                                                                                <td>
                                                                                    <div className={styles['qty-edit-controls']}>
                                                                                        <button onClick={() => handleItemQuantityChange(idx, item.Cantidad - 1)}>-</button>
                                                                                        <input
                                                                                            type="number"
                                                                                            value={item.Cantidad}
                                                                                            onChange={(e) => handleItemQuantityChange(idx, parseInt(e.target.value) || 0)}
                                                                                        />
                                                                                        <button onClick={() => handleItemQuantityChange(idx, item.Cantidad + 1)}>+</button>
                                                                                    </div>
                                                                                </td>
                                                                                <td>
                                                                                    {puedeDescontar ? (
                                                                                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.8em', whiteSpace: 'nowrap' }}>
                                                                                            <input
                                                                                                type="checkbox"
                                                                                                checked={(item.Descuento || 0) > 0}
                                                                                                onChange={() => toggleEditItemDiscount(idx)}
                                                                                                style={{ accentColor: '#27ae60' }}
                                                                                            />
                                                                                            {(item.Descuento || 0) > 0
                                                                                                ? <span style={{ color: '#27ae60', fontWeight: 'bold' }}>-${(item.Descuento || 0).toLocaleString()}</span>
                                                                                                : <span style={{ color: '#aaa' }}>-$0</span>
                                                                                            }
                                                                                        </label>
                                                                                    ) : (
                                                                                        <span style={{ color: '#ccc', fontSize: '0.8em' }}>—</span>
                                                                                    )}
                                                                                </td>
                                                                                <td>${subtotal.toLocaleString()}</td>
                                                                                <td>
                                                                                    <button onClick={() => handleRemoveItem(idx)} className={styles['btn-remove-item']}>×</button>
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>

                                                        <div className={styles['edit-actions']}>
                                                            <button className={`${styles['btn-action']} ${styles['btn-cancel']}`} onClick={handleCancelEdit}>Cancelar</button>
                                                            <button className={`${styles['btn-action']} ${styles['btn-save']}`} onClick={handleSaveEdit}>Guardar Cambios</button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className={styles['details-content']}>
                                                        <div className={styles['details-column']}>
                                                            <div className={styles['details-block']}>
                                                                <h4><FaUser /> Datos del Cliente</h4>
                                                                <p><strong>Nombre:</strong> {order.Cliente}</p>
                                                                <p><strong>DNI:</strong> {order.DNI || '-'}</p>
                                                                <p><strong>Email:</strong> {order.Email || '-'}</p>
                                                                <p><strong>Teléfono:</strong> {order.Telefono || '-'}</p>
                                                            </div>
                                                            <div className={styles['details-block']}>
                                                                <h4><FaMapMarkerAlt /> Envío / Entrega</h4>
                                                                <p><strong>Tipo:</strong> {order.TipoPedido}</p>
                                                                {order.Direccion && <p><strong>Dirección:</strong> {order.Direccion}</p>}
                                                                {order.FechaEntrega && <p><strong>Fecha Entrega:</strong> {order.FechaEntrega}</p>}
                                                                {order.HorarioEntrega && <p><strong>Horario:</strong> {order.HorarioEntrega}</p>}
                                                                {order.RestriccionHorario && (
                                                                    <p><strong>Restricción:</strong> <span className={styles['restriccion-horario-badge']}>⏰ {order.RestriccionHorario}</span></p>
                                                                )}
                                                                {order.CostoEnvio && order.CostoEnvio > 0 ? <p><strong>Costo Envío:</strong> ${order.CostoEnvio.toLocaleString()}</p> : null}
                                                                {order.TipoPedido === 'Retiro' && (
                                                                    <p><strong>Estado de retiro:</strong>{' '}
                                                                        <span style={{
                                                                            padding: '2px 8px',
                                                                            borderRadius: '4px',
                                                                            color: 'white',
                                                                            fontWeight: 700,
                                                                            fontSize: '0.82em',
                                                                            backgroundColor: order.retirado === 1 ? '#27ae60' : '#e74c3c'
                                                                        }}>
                                                                            {order.retirado === 1 ? '✓ Ya lo pasaron a buscar' : 'Pendiente de retiro'}
                                                                        </span>
                                                                    </p>
                                                                )}
                                                                <p><strong>Local:</strong> {locales.find(l => l.locales_id == order.id_local)?.nombre_local || 'Global / Sin asignar'}</p>
                                                            </div>
                                                        </div>

                                                        <div className={styles['details-column']}>
                                                            <div className={styles['details-block']}>
                                                                <h4>Productos</h4>
                                                                <ul className={styles['details-list']}>
                                                                    {order.Items.map((item, idx) => (
                                                                        <li key={idx}>
                                                                            <span>
                                                                                <strong>{item.Cantidad}x</strong> {item.Producto}
                                                                                {((item.DetallePromocion || item.PromoSelecciones || []).length > 0) && (
                                                                                    <ul className={styles['promo-selections']}>
                                                                                        {(item.DetallePromocion || item.PromoSelecciones || []).map((selection, selectionIdx) => (
                                                                                            <li key={`${selection.id_producto}-${selectionIdx}`}>
                                                                                                {selection.grupo ? `${selection.grupo}: ` : ''}{selection.cantidad}x {selection.nombre}
                                                                                            </li>
                                                                                        ))}
                                                                                    </ul>
                                                                                )}
                                                                            </span>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                            <div className={styles['details-block']}>
                                                                <h4><FaMoneyBillWave /> Pago</h4>
                                                                <p><strong>Total:</strong> ${Number(order.Total).toLocaleString()}</p>
                                                                <p><strong>Método:</strong> {order.MetodoPago}</p>
                                                                {order.MetodoPago === 'Mixto' && order.pagos_mixtos && order.pagos_mixtos.length > 0 && (
                                                                    <ul style={{ margin: '4px 0 0 12px', padding: 0, listStyle: 'disc', fontSize: '0.9em' }}>
                                                                        {order.pagos_mixtos.map((p, i) => (
                                                                            <li key={i}>{p.metodo}: ${Number(p.monto).toLocaleString()}</li>
                                                                        ))}
                                                                    </ul>
                                                                )}
                                                                <p><strong>Estado:</strong> {order.Pagado === 'Si' ? 'Pagado' : 'Pendiente'}</p>
                                                                <p style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                    <strong>Comprobante:</strong>
                                                                    {order.ComprobanteUrl ? (
                                                                        <a href={`${API_URL}${order.ComprobanteUrl}`} target="_blank" rel="noopener noreferrer" style={{ color: '#27ae60', fontWeight: 600 }}>
                                                                            <FaFileInvoiceDollar style={{ marginRight: '4px' }} />Ver
                                                                        </a>
                                                                    ) : (
                                                                        <button
                                                                            onClick={() => handleComprobanteClick(order.ID)}
                                                                            disabled={uploadingComprobante === order.ID}
                                                                            style={{ background: 'none', border: 'none', color: '#607d8b', cursor: 'pointer', padding: 0, fontSize: '0.9em' }}
                                                                        >
                                                                            <FaPaperclip style={{ marginRight: '4px' }} />
                                                                            {uploadingComprobante === order.ID ? 'Subiendo...' : 'Subir'}
                                                                        </button>
                                                                    )}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        {(order.Observaciones) && (
                                                            <div className={`${styles['details-column']} ${styles['full-width']}`}>
                                                                <div className={styles['details-block']}>
                                                                    <h4>Observaciones</h4>
                                                                    <p>{order.Observaciones}</p>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                                );
                            })
                        )}
                    </tbody>
                </table>
                <Pagination
                    meta={pagination}
                    onPageChange={setPage}
                    onPerPageChange={setPerPage}
                    itemLabel="pedidos"
                />
            </div>
        </div>
    );
};

export default OrdersList;
